/**
 * The CMS page builder: every heading, paragraph and FAQ the public site shows.
 *
 * **Publishing is not a deploy, and this screen says so.** Publishing writes rows — the edge tier
 * serves the new copy within its cache window, and the prerendered site changes at the next
 * `make site-deploy`. An operator who publishes a correction and then reloads the live site would
 * otherwise conclude the CMS is broken.
 *
 * **Archiving makes you choose what the URL does next.** There is no default, because the default
 * would be a silent 404 — and an indexed URL is an asset somebody earned over months. 301 to
 * whatever replaced it, or 410 to say deliberately gone.
 *
 * **The preview is the site's own render.** The server renders the draft through the exporter's
 * `Renderer` (CSR-041) and this screen only frames the HTML it returns, in a sandbox with scripts
 * off: the console never runs a page's code with an operator's session beside it.
 */
import { useState } from 'react';
import { StatusChip, type Tone } from '../components/primitives';
import { Input, Select } from '../components/forms';
import { DataTable, DetailPage } from '../components/data';
import { KV } from '../components/entities';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, FilterBar, ScopeAction, Section } from '../components/kit';
import { Modal } from '../components/feedback';
import { useConsole } from '../app/console';
import { can } from '../lib/rbac';
import { errorMessage } from '../lib/api/client';
import {
  useArchivePage,
  useDeletePage,
  usePurgePage,
  useRestorePage,
  useCmsPage,
  useCmsPages,
  useCreateBlock,
  useCreateFaq,
  useCreatePage,
  useDeleteBlock,
  usePreviewPage,
  usePublishPage,
  useReorderBlocks,
  useRestoreRevision,
  useUpdateBlock,
  useUpdateFaq,
  useUpdatePage,
  type CmsPageRow,
} from '../lib/api/hooks/cms';
import {
  usePageMeta,
  usePutPageMeta,
  useRebuildSite,
  useSeoBudget,
  type SeoBudget,
  type SeoPageMetaIn,
} from '../lib/api/hooks/seo';
import { meterSentence } from './SeoBudget';
import { ROBOTS } from './SeoTemplates';
import { useUrlSearch } from '../lib/filterCodecs';
import { useEnumLabels, type EnumLabelRow } from '../lib/api/hooks/enumLabels';
import type { components } from '../api/generated/schema';
import { formatDate, formatDateTime } from '../lib/datetime';

const PAGE_SIZE = 50;

type FaqGroup = components['schemas']['FaqGroup'];

/** `FaqGroup` in the server's order (CSR-162). The record makes a fifth key a type error here. */
const FAQ_GROUP_ORDER: Readonly<Record<FaqGroup, number>> = {
  booking: 0,
  payments_refunds: 1,
  for_pandits: 2,
  trust_verification: 3,
};
export const FAQ_GROUPS = (Object.keys(FAQ_GROUP_ORDER) as FaqGroup[]).sort(
  (a, b) => FAQ_GROUP_ORDER[a] - FAQ_GROUP_ORDER[b],
);
const NO_GROUP = 'No group';

/**
 * The word the picker shows for each group: the `faq_group` label in the page's own language,
 * else English, else the key itself. The heading a reader sees is the block's own heading;
 * this is only the name the console files the question under.
 */
export function faqGroupLabels(rows: readonly EnumLabelRow[], locale: string): Record<FaqGroup, string> {
  const pick = (key: FaqGroup) =>
    rows.find((r) => r.enum_value === key && r.locale === locale)?.label ??
    rows.find((r) => r.enum_value === key && r.locale === 'en')?.label ??
    key;
  return Object.fromEntries(FAQ_GROUPS.map((key) => [key, pick(key)])) as Record<FaqGroup, string>;
}
const ALL = 'every kind';

/** The server's vocabulary — ops read the same words in the audit log. */
const PAGE_TYPES = [
  ALL,
  'home',
  'city_explore',
  'puja_detail',
  'puja_city_landing',
  'temple_detail',
  'state_directory',
  'pandit_profile',
  'occasion',
  'language_sampradaya',
  'search_listing',
  'legal',
  'custom',
];

const BLOCK_TYPES = [
  'hero',
  'rich_text',
  'faq_list',
  'puja_grid',
  'pandit_grid',
  'temple_info',
  'cta_install',
  'testimonial',
  'image_band',
  'trust_band',
  'muhurat_note',
];

/**
 * A legal document's own dates (CSR-176, website doc 15 §2.1.2). The site prints "Effective
 * {date}" and "Reviewed by counsel on {date}." only when the row records them, so an empty
 * field is the honest state, and clearing one sends an explicit null.
 */
type LegalDate = 'effective_on' | 'counsel_reviewed_on';
const LEGAL_DATES: readonly { key: LegalDate; label: string; field: string; helper: string }[] = [
  {
    key: 'effective_on',
    label: 'Effective',
    field: 'Effective on',
    helper: 'Printed as “Effective {date}” on the page. Leave empty for no line.',
  },
  {
    key: 'counsel_reviewed_on',
    label: 'Reviewed by counsel',
    field: 'Reviewed by counsel on',
    helper: 'Printed as “Reviewed by counsel on {date}.” Enter only the date counsel signed off.',
  },
];

type PageEdit = { title: string; path: string } & Record<LegalDate, string>;

/** Only the dates the editor changed: an untouched date is not re-sent, a cleared one is null. */
export function changedDates(page: CmsPageRow, edit: PageEdit): Partial<Record<LegalDate, string | null>> {
  const out: Partial<Record<LegalDate, string | null>> = {};
  for (const { key } of LEGAL_DATES) {
    const next = edit[key] || null;
    if (next !== (page[key] ?? null)) out[key] = next;
  }
  return out;
}

export function statusTone(status: string): Tone {
  if (status === 'published') return 'success';
  if (status === 'archived') return 'neutral';
  return 'warn';
}

/**
 * What archiving this URL will do, said before the operator commits.
 *
 * Exported so all three readings are asserted: a 301 names its destination, a 410 says gone and
 * names none, and a 301 with nothing typed says what is still missing rather than silently
 * doing nothing.
 */
export function redirectConsequence(httpStatus: number, toPath: string): string {
  if (httpStatus === 410) {
    return 'The URL will answer 410 — deliberately gone. Search engines drop it, which is the honest outcome when nothing replaced it.';
  }
  if (toPath.trim() === '') {
    return 'A redirect needs somewhere to go. If nothing replaced this page, choose 410 instead.';
  }
  const kind = httpStatus === 301 ? 'permanently' : 'temporarily';
  return `Anyone opening this URL — and every search engine holding it — goes ${kind} to ${toPath.trim()}.`;
}

/** What still holds a deleted page, in words. A kind the console does not know is shown as sent. */
const BLOCKER_WORDS: Record<string, string> = { redirect_rule: 'a redirect rule', analytics: 'analytics' };
export function heldBy(kinds: readonly string[]): string {
  return `Held by ${kinds.map((k) => BLOCKER_WORDS[k] ?? k).join(', ')}`;
}

export function Cms() {
  const { scope, toast } = useConsole();
  const [kind, setKind] = useState(ALL);
  const [q, setQ] = useUrlSearch('cms');
  const [includeArchived, setIncludeArchived] = useState(false);
  /* Deleted drafts are their own list, never mixed into the live one: the detail read does not
     serve them, so the only thing to do with one is purge it (R141). */
  const [showDeleted, setShowDeleted] = useState(false);
  const [purging, setPurging] = useState<CmsPageRow | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [httpStatus, setHttpStatus] = useState(301);
  const [toPath, setToPath] = useState('');
  const [blockType, setBlockType] = useState(BLOCK_TYPES[0]!);
  /* One block open for its words at a time. Blank is null: the renderer omits a missing
     heading, and an empty string would paint an empty <h2>. */
  const [faqEdit, setFaqEdit] = useState<{ id: number; question: string; answer: string } | null>(null);
  const [blockEdit, setBlockEdit] = useState<{ id: number; heading: string; body: string } | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ page_type: 'legal', path: '', title: '' });
  const [edit, setEdit] = useState<PageEdit | null>(null);
  const [faq, setFaq] = useState<{ question: string; answer: string; group: FaqGroup | null }>({
    question: '',
    answer: '',
    group: null,
  });
  const faqGroupRows = useEnumLabels('faq_group');

  const list = useCmsPages({
    page: 1,
    page_size: PAGE_SIZE,
    ...(kind === ALL ? {} : { page_type: kind }),
    ...(q === '' ? {} : { q }),
    ...(showDeleted ? { deleted: true } : includeArchived ? { include_archived: true } : {}),
  });
  const detail = useCmsPage(openId);
  const publish = usePublishPage();
  const preview = usePreviewPage();
  const archive = useArchivePage();
  const restorePage = useRestorePage();
  const deletePage = useDeletePage();
  const purge = usePurgePage();
  const addBlock = useCreateBlock();
  const editBlock = useUpdateBlock();
  const removeBlock = useDeleteBlock();
  const reorder = useReorderBlocks();
  const restore = useRestoreRevision();
  const create = useCreatePage();
  const update = useUpdatePage();
  const addFaq = useCreateFaq();
  const editFaq = useUpdateFaq();

  const fail = (error: unknown) => toast({ tone: 'error', message: errorMessage(error) });

  if (openId !== null) {
    const page = detail.data?.page;
    const isDraft = page?.status === 'draft';
    const groupLabel = faqGroupLabels(faqGroupRows.data?.items ?? [], page?.locale ?? 'en');
    const groupOptions = [NO_GROUP, ...FAQ_GROUPS.map((key) => groupLabel[key])];
    const groupOf = (label: string): FaqGroup | null =>
      FAQ_GROUPS.find((key) => groupLabel[key] === label) ?? null;
    const labelOf = (group: FaqGroup | null | undefined) => (group ? groupLabel[group] : NO_GROUP);

    return (
      <DetailPage
        title={page?.path ?? `Page ${openId}`}
        onClose={() => {
          setOpenId(null);
          setToPath('');
        }}
        actions={
          detail.isSuccess && page !== undefined ? (
            <>
              <ScopeAction
                scope={scope}
                need="catalogue_content"
                size="sm"
                variant="secondary"
                disabled={preview.isPending}
                onClick={() => preview.mutate(openId, { onSuccess: setPreviewHtml, onError: fail })}
              >
                {preview.isPending ? 'Rendering…' : 'Preview'}
              </ScopeAction>
              {page.status === 'archived' && (
                <ScopeAction
                  scope={scope}
                  need="catalogue_content"
                  size="sm"
                  variant="secondary"
                  disabled={restorePage.isPending}
                  onClick={() =>
                    restorePage.mutate(openId, {
                      onSuccess: () =>
                        toast({
                          tone: 'success',
                          message: 'Restored to draft — publish it when it is ready',
                        }),
                      onError: fail,
                    })
                  }
                >
                  Restore to draft
                </ScopeAction>
              )}
              {page.status !== 'published' && page.status !== 'archived' && (
                <ScopeAction
                  scope={scope}
                  need="catalogue_content"
                  size="sm"
                  onClick={() => setPublishing(true)}
                >
                  Publish
                </ScopeAction>
              )}
              {page.status === 'published' && (
                <ScopeAction
                  scope={scope}
                  need="catalogue_content"
                  size="sm"
                  variant="ghost"
                  onClick={() => setArchiving(true)}
                >
                  Archive
                </ScopeAction>
              )}
              {isDraft && (
                <ScopeAction
                  scope={scope}
                  need="catalogue_content"
                  size="sm"
                  variant="ghost"
                  onClick={() => setDeleting(true)}
                >
                  Delete draft
                </ScopeAction>
              )}
            </>
          ) : null
        }
      >
        {detail.isPending && <LoadingState message="Reading the page…" />}
        {detail.isError && (
          <ErrorState title="Could not read the page" cause={errorMessage(detail.error)} />
        )}

        {detail.isSuccess && page !== undefined && (
          <>
            <Section
              title={page.title}
              note={
                page.status === 'published'
                  ? 'Live on the edge now · the prerendered site updates at the next deploy'
                  : 'Not live. Publishing lints the whole page and checks its SEO template first.'
              }
            >
              {edit === null ? (
                <>
                  <KV
                    rows={[
                      ['Path', page.path],
                      ['Kind', page.page_type.replace(/_/g, ' ')],
                      ['Locale', page.locale],
                      ['State', page.status],
                      ['Published', page.published_at ? formatDateTime(page.published_at) : 'never'],
                      ...(page.page_type === 'legal'
                        ? LEGAL_DATES.map(
                            ({ key, label }): [string, string] => [label, page[key] ?? 'not recorded'],
                          )
                        : []),
                    ]}
                  />
                  <ScopeAction
                    scope={scope}
                    need="catalogue_content"
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setEdit({
                        title: page.title,
                        path: page.path,
                        effective_on: page.effective_on ?? '',
                        counsel_reviewed_on: page.counsel_reviewed_on ?? '',
                      })
                    }
                  >
                    Edit
                  </ScopeAction>
                </>
              ) : (
                <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
                  <Input
                    label="Title"
                    value={edit.title}
                    onChange={(next) => setEdit({ ...edit, title: next })}
                  />
                  <Input
                    label="Path"
                    value={edit.path}
                    onChange={(next) => setEdit({ ...edit, path: next })}
                    helper="Validated against the URL taxonomy for this page kind"
                  />
                  {page.page_type === 'legal' &&
                    LEGAL_DATES.map(({ key, field, helper }) => (
                      <Input
                        key={key}
                        type="date"
                        label={field}
                        value={edit[key]}
                        onChange={(next) => setEdit({ ...edit, [key]: next })}
                        helper={helper}
                      />
                    ))}
                  <div style={{ display: 'flex', gap: 'var(--sp-md)' }}>
                    <ScopeAction
                      scope={scope}
                      need="catalogue_content"
                      size="sm"
                      onClick={() =>
                        update.mutate(
                          { id: page.id, title: edit.title, path: edit.path, ...changedDates(page, edit) },
                          {
                            onSuccess: () => {
                              setEdit(null);
                              toast({ tone: 'success', message: 'Saved' });
                            },
                            onError: fail,
                          },
                        )
                      }
                    >
                      Save
                    </ScopeAction>
                    <ScopeAction
                      scope={scope}
                      need="catalogue_content"
                      size="sm"
                      variant="ghost"
                      onClick={() => setEdit(null)}
                    >
                      Cancel
                    </ScopeAction>
                  </div>
                </div>
              )}
            </Section>

            <PageSeo cmsPageId={page.id} />

            <Section
              title="Blocks"
              note={
                isDraft
                  ? 'A draft block can be deleted outright.'
                  : 'This page is live, so a block is hidden rather than deleted — deleting destroys the history its revisions hang from.'
              }
            >
              {detail.data.blocks.length === 0 ? (
                <EmptyState
                  inline
                  title="No blocks yet"
                  body="A page with no blocks publishes, and renders nothing but its heading."
                />
              ) : (
                detail.data.blocks.map((b, index) => (
                  <div key={b.id}>
                  <div
                    style={{
                      display: 'flex',
                      gap: 'var(--sp-md)',
                      alignItems: 'center',
                      marginBottom: 'var(--sp-sm)',
                      opacity: b.is_visible ? 1 : 0.5,
                    }}
                  >
                    <span style={{ flex: 1, fontSize: 'var(--fs-admin-ui)' }}>
                      {b.block_type.replace(/_/g, ' ')}
                      {b.heading ? ` · ${b.heading}` : ''}
                      {b.is_visible ? '' : ' · hidden'}
                    </span>
                    <ScopeAction
                      scope={scope}
                      need="catalogue_content"
                      size="sm"
                      variant="ghost"
                      ariaLabel={`Edit the ${b.block_type.replace(/_/g, ' ')} block`}
                      onClick={() => setBlockEdit({ id: b.id, heading: b.heading ?? '', body: b.body ?? '' })}
                    >
                      Edit
                    </ScopeAction>
                    <ScopeAction
                      scope={scope}
                      need="catalogue_content"
                      size="sm"
                      variant="ghost"
                      disabled={index === 0}
                      onClick={() => {
                        /* The complete order, never a move — see the hook. */
                        const ids = detail.data.blocks.map((x) => x.id);
                        const swapped = [...ids];
                        const previous = swapped[index - 1]!;
                        swapped[index - 1] = swapped[index]!;
                        swapped[index] = previous;
                        reorder.mutate(
                          { pageId: page.id, blockIds: swapped },
                          { onError: fail },
                        );
                      }}
                    >
                      Move up
                    </ScopeAction>
                    <ScopeAction
                      scope={scope}
                      need="catalogue_content"
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        isDraft
                          ? removeBlock.mutate(b.id, { onError: fail })
                          : editBlock.mutate(
                              { id: b.id, is_visible: !b.is_visible },
                              { onError: fail },
                            )
                      }
                    >
                      {isDraft ? 'Delete' : b.is_visible ? 'Hide' : 'Show'}
                    </ScopeAction>
                  </div>
                  {blockEdit?.id === b.id && (
                    <div style={{ display: 'grid', gap: 'var(--sp-sm)', marginBottom: 'var(--sp-md)' }}>
                      <Input
                        label="Heading"
                        value={blockEdit.heading}
                        onChange={(heading) => setBlockEdit({ ...blockEdit, heading })}
                      />
                      <Input
                        label="Body"
                        value={blockEdit.body}
                        onChange={(body) => setBlockEdit({ ...blockEdit, body })}
                        multiline
                        helper="Linted like any other shipped copy when it is saved"
                      />
                      <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
                        <ScopeAction
                          scope={scope}
                          need="catalogue_content"
                          size="sm"
                          disabled={editBlock.isPending}
                          onClick={() =>
                            editBlock.mutate(
                              {
                                id: b.id,
                                heading: blockEdit.heading.trim() || null,
                                body: blockEdit.body.trim() || null,
                              },
                              {
                                onSuccess: () => {
                                  setBlockEdit(null);
                                  toast({ tone: 'success', message: 'Block saved' });
                                },
                                onError: fail,
                              },
                            )
                          }
                        >
                          Save block
                        </ScopeAction>
                        <ScopeAction scope={scope} need="catalogue_content" size="sm" variant="ghost" onClick={() => setBlockEdit(null)}>
                          Cancel
                        </ScopeAction>
                      </div>
                    </div>
                  )}
                  </div>
                ))
              )}

              <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end' }}>
                <Select
                  label="Add a block"
                  value={blockType}
                  options={BLOCK_TYPES}
                  onChange={setBlockType}
                />
                <ScopeAction
                  scope={scope}
                  need="catalogue_content"
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    addBlock.mutate(
                      { pageId: page.id, block_type: blockType },
                      { onError: fail },
                    )
                  }
                >
                  Add
                </ScopeAction>
              </div>
            </Section>

            <Section title="FAQs" note="Answers are linted like any other shipped copy">
              {detail.data.faqs.map((f) => (
                <div key={f.id}>
                <div
                  style={{
                    display: 'flex',
                    gap: 'var(--sp-md)',
                    alignItems: 'center',
                    marginBottom: 'var(--sp-sm)',
                  }}
                >
                  <span style={{ flex: 1, fontSize: 'var(--fs-admin-ui)' }}>
                    {f.question}
                    <span
                      style={{ color: 'var(--c-ink2)', fontSize: 'var(--fs-admin-colhead)' }}
                    >
                      {' '}
                      · {f.is_published ? 'published' : 'not published'}
                    </span>
                  </span>
                  <Select
                    ariaLabel={`Group of “${f.question}”`}
                    value={labelOf(f.group_key)}
                    options={groupOptions}
                    disabled={!can(scope, 'catalogue_content')}
                    onChange={(next) =>
                      editFaq.mutate({ id: f.id, group_key: groupOf(next) }, { onError: fail })
                    }
                  />
                  <ScopeAction
                    scope={scope}
                    need="catalogue_content"
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      editFaq.mutate(
                        { id: f.id, is_published: !f.is_published },
                        { onError: fail },
                      )
                    }
                  >
                    {f.is_published ? 'Unpublish' : 'Publish'}
                  </ScopeAction>
                  <ScopeAction
                    scope={scope}
                    need="catalogue_content"
                    size="sm"
                    variant="ghost"
                    ariaLabel={`Edit “${f.question}”`}
                    onClick={() => setFaqEdit({ id: f.id, question: f.question, answer: f.answer })}
                  >
                    Edit
                  </ScopeAction>
                </div>
                {faqEdit?.id === f.id && (
                  <div style={{ display: 'grid', gap: 'var(--sp-sm)', marginBottom: 'var(--sp-md)' }}>
                    <Input
                      label="Edit question"
                      value={faqEdit.question}
                      onChange={(question) => setFaqEdit({ ...faqEdit, question })}
                    />
                    <Input
                      label="Edit answer"
                      value={faqEdit.answer}
                      onChange={(answer) => setFaqEdit({ ...faqEdit, answer })}
                      multiline
                    />
                    <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
                      <ScopeAction
                        scope={scope}
                        need="catalogue_content"
                        size="sm"
                        disabled={faqEdit.question.trim() === '' || faqEdit.answer.trim() === ''}
                        onClick={() =>
                          editFaq.mutate(
                            { id: f.id, question: faqEdit.question.trim(), answer: faqEdit.answer.trim() },
                            {
                              onSuccess: () => {
                                setFaqEdit(null);
                                toast({ tone: 'success', message: 'FAQ saved' });
                              },
                              onError: fail,
                            },
                          )
                        }
                      >
                        Save FAQ
                      </ScopeAction>
                      <ScopeAction scope={scope} need="catalogue_content" size="sm" variant="ghost" onClick={() => setFaqEdit(null)}>
                        Close
                      </ScopeAction>
                    </div>
                  </div>
                )}
                </div>
              ))}

              <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
                <Input
                  label="Question"
                  value={faq.question}
                  onChange={(next) => setFaq({ ...faq, question: next })}
                />
                <Input
                  label="Answer"
                  value={faq.answer}
                  onChange={(next) => setFaq({ ...faq, answer: next })}
                />
                <Select
                  label="Group"
                  value={labelOf(faq.group)}
                  options={groupOptions}
                  onChange={(next) => setFaq({ ...faq, group: groupOf(next) })}
                />
                <ScopeAction
                  scope={scope}
                  need="catalogue_content"
                  size="sm"
                  variant="secondary"
                  disabled={faq.question.trim() === '' || faq.answer.trim() === ''}
                  onClick={() =>
                    addFaq.mutate(
                      {
                        cms_page_id: page.id,
                        locale: page.locale,
                        question: faq.question,
                        answer: faq.answer,
                        ...(faq.group ? { group_key: faq.group } : {}),
                      },
                      {
                        onSuccess: () => {
                          setFaq({ question: '', answer: '', group: null });
                          toast({ tone: 'success', message: 'Added' });
                        },
                        onError: fail,
                      },
                    )
                  }
                >
                  Add the FAQ
                </ScopeAction>
              </div>
            </Section>

            <Section
              title="History"
              note="Restoring copies an old version forward — the history never moves backwards"
            >
              {detail.data.revisions.length === 0 ? (
                <EmptyState inline title="No edits yet" body="Nothing has changed since it was created." />
              ) : (
                detail.data.revisions.map((r) => (
                  <div
                    key={r.id}
                    style={{
                      display: 'flex',
                      gap: 'var(--sp-md)',
                      alignItems: 'center',
                      marginBottom: 'var(--sp-xs)',
                    }}
                  >
                    <span style={{ flex: 1, fontSize: 'var(--fs-admin-ui)' }}>
                      {formatDateTime(r.created_at)} · admin {r.admin_user_id}
                      {r.change_note ? ` · ${r.change_note}` : ''}
                    </span>
                    <ScopeAction
                      scope={scope}
                      need="catalogue_content"
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        restore.mutate(r.id, {
                          onSuccess: () => toast({ tone: 'success', message: 'Copied forward' }),
                          onError: fail,
                        })
                      }
                    >
                      Restore
                    </ScopeAction>
                  </div>
                ))
              )}
            </Section>
          </>
        )}

        {previewHtml !== null && (
          <Modal title="Preview — not live" width={1100} onClose={() => setPreviewHtml(null)}>
            <p style={{ margin: '0 0 var(--sp-sm)', fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
              Rendered by the site&rsquo;s own templates from this draft. Nothing was published or
              stored, and scripts are off in the frame.
            </p>
            <iframe
              title="Draft preview"
              sandbox=""
              srcDoc={previewHtml}
              style={{ width: '100%', height: '70vh', border: '1px solid var(--c-border)', borderRadius: 'var(--r-tile)', background: 'var(--c-surface)' }}
            />
          </Modal>
        )}

        {publishing && (
          <ConfirmAction
            open
            title="Publish this page?"
            confirmLabel="Publish"
            phrase="PUBLISH"
            consequences={[
              'Every heading, body and FAQ answer is checked for outcome claims; the publish is refused if one is found.',
              'The page must have an active SEO template for its kind and locale, or publishing is refused.',
              'It goes live on the edge within the cache window — the prerendered site changes at the next deploy.',
            ]}
            onCancel={() => setPublishing(false)}
            onConfirm={() =>
              publish.mutate(openId, {
                onSuccess: () => {
                  setPublishing(false);
                  toast({ tone: 'success', message: 'Published' });
                },
                onError: (error) => {
                  setPublishing(false);
                  fail(error);
                },
              })
            }
          />
        )}

        {deleting && page !== undefined && (
          <ConfirmAction
            open
            title={`Delete the draft ${page.path}?`}
            confirmLabel="Delete draft"
            phrase="DELETE"
            tone="error"
            consequences={[
              'Only for a draft that has never been published — the server refuses anything that was ever live; archive that instead.',
              'The path is freed for a new page. The revisions are kept on the server.',
            ]}
            onCancel={() => setDeleting(false)}
            onConfirm={() => {
              setDeleting(false);
              deletePage.mutate(openId, {
                onSuccess: () => {
                  toast({ tone: 'info', message: `${page.path} deleted` });
                  setOpenId(null);
                },
                onError: fail,
              });
            }}
          />
        )}

        {archiving && (
          <ConfirmAction
            open
            title="Archive this page?"
            confirmLabel="Archive"
            phrase="ARCHIVE"
            tone="error"
            consequences={[
              redirectConsequence(httpStatus, toPath),
              'The URL stays registered, so it keeps redirecting and still counts against the page budget.',
              'The page stops rendering immediately on the edge and disappears from the site at the next deploy.',
            ]}
            onCancel={() => setArchiving(false)}
            onConfirm={() =>
              archive.mutate(
                {
                  id: openId,
                  httpStatus,
                  toPath: httpStatus === 410 ? null : toPath.trim() || null,
                  note: '',
                },
                {
                  onSuccess: () => {
                    setArchiving(false);
                    toast({ tone: 'success', message: 'Archived' });
                  },
                  onError: (error) => {
                    setArchiving(false);
                    fail(error);
                  },
                },
              )
            }
          >
            <Select
              label="What the URL does next"
              value={String(httpStatus)}
              options={['301', '302', '410']}
              onChange={(next) => setHttpStatus(Number(next))}
            />
            {httpStatus !== 410 && (
              <Input label="Goes to" value={toPath} onChange={setToPath} placeholder="/legal/privacy" />
            )}
          </ConfirmAction>
        )}
      </DetailPage>
    );
  }

  return (
    <>
      <DeployStatus />
      <FilterBar
        q={q}
        onQ={setQ}
        placeholder="Path or title — searched on the server"
        extra={<Select label="Kind" value={kind} options={PAGE_TYPES} onChange={setKind} />}
        right={
          <div style={{ display: 'flex', gap: 'var(--sp-md)' }}>
            {!showDeleted && (
              <ScopeAction
                scope={scope}
                need="catalogue_content"
                size="sm"
                variant="ghost"
                onClick={() => setIncludeArchived((on) => !on)}
              >
                {includeArchived ? 'Hide archived' : 'Show archived'}
              </ScopeAction>
            )}
            <ScopeAction
              scope={scope}
              need="catalogue_content"
              size="sm"
              variant="ghost"
              onClick={() => setShowDeleted((on) => !on)}
            >
              {showDeleted ? 'Hide deleted' : 'Show deleted'}
            </ScopeAction>
            <ScopeAction
              scope={scope}
              need="catalogue_content"
              size="sm"
              onClick={() => setCreating(true)}
            >
              New page
            </ScopeAction>
          </div>
        }
      />

      {creating && (
        <Modal
          title="A new page"
          onClose={() => setCreating(false)}
          actions={
            <ScopeAction
              scope={scope}
              need="catalogue_content"
              size="md"
              disabled={draft.path.trim() === '' || draft.title.trim() === ''}
              onClick={() =>
                create.mutate(
                  {
                    page_type: draft.page_type,
                    path: draft.path.trim(),
                    // Derived, never typed twice. A slug that could disagree with the last
                    // segment of its own path is a second source of truth for the same fact.
                    slug: draft.path.trim().split('/').pop(),
                    title: draft.title.trim(),
                  },
                  {
                    onSuccess: (made) => {
                      setCreating(false);
                      setDraft({ page_type: 'legal', path: '', title: '' });
                      setOpenId(made.id);
                    },
                    onError: fail,
                  },
                )
              }
            >
              Create the draft
            </ScopeAction>
          }
        >
          <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
            {/* Created as a draft, always. A create that could publish would let a page reach
                the index with nobody having read it back. */}
            <p style={{ margin: 0, fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink2)' }}>
              It starts as a draft. Publishing is a separate act with its own checks.
            </p>
            <Select
              label="Kind"
              value={draft.page_type}
              options={PAGE_TYPES.filter((t) => t !== ALL)}
              onChange={(next) => setDraft({ ...draft, page_type: next })}
            />
            <Input
              label="Path"
              value={draft.path}
              onChange={(next) => setDraft({ ...draft, path: next })}
              placeholder="/legal/privacy"
              helper="Validated against the URL taxonomy for this kind"
            />
            <Input
              label="Title"
              value={draft.title}
              onChange={(next) => setDraft({ ...draft, title: next })}
              helper="The admin title. The SEO title comes from the template."
            />
          </div>
        </Modal>
      )}

      {list.isPending && <LoadingState message="Reading the pages…" />}
      {list.isError && (
        <ErrorState title="Could not read the pages" cause={errorMessage(list.error)} />
      )}
      {list.isSuccess && list.data.items.length === 0 && showDeleted && (
        <EmptyState title="No deleted pages" body="A draft deleted in error lands here until it is purged." />
      )}
      {list.isSuccess && list.data.items.length === 0 && !showDeleted && (
        <EmptyState
          title="No pages match"
          body="Every page the public site shows is a row here — search is on the server, so this is the whole set, not the page in hand."
        />
      )}
      {list.isSuccess && list.data.items.length > 0 && showDeleted && (
        <DataTable
          density="compact"
          columns={[
            { key: 'path', label: 'URL', mono: true },
            { key: 'title', label: 'Title' },
            { key: 'deleted', label: 'Deleted' },
            { key: 'purge', label: '' },
          ]}
          rows={list.data.items.map((p) => ({
            id: p.id,
            path: p.path,
            title: p.title,
            deleted: formatDate(p.deleted_at!),
            /* A page something still names cannot go, so it gets the reason instead of a button
               that would only come back 409. */
            purge:
              p.purge_blockers!.length > 0 ? (
                heldBy(p.purge_blockers!)
              ) : (
                <ScopeAction scope={scope} need="catalogue_content" size="sm" variant="secondary" onClick={() => setPurging(p)}>
                  Purge for good
                </ScopeAction>
              ),
          }))}
          footer={`${list.data.total} deleted page${list.data.total === 1 ? '' : 's'}`}
        />
      )}
      <ConfirmAction
        open={purging !== null}
        title={`Purge ${purging?.path ?? ''} for good?`}
        phrase="PURGE"
        tone="error"
        reason
        consequences={[
          'Removes the page with its blocks, SEO row, FAQs and its own revisions. It cannot be undone.',
          'It was never published, so no URL on the live site changes and no cache is touched.',
          'The audit log keeps the path, the title, how many revisions went, and your reason.',
        ]}
        onCancel={() => setPurging(null)}
        onConfirm={(reason) =>
          purge.mutate(
            { id: purging!.id, reason },
            {
              onSuccess: () => {
                toast({ tone: 'info', message: `${purging!.path} purged` });
                setPurging(null);
              },
              onError: fail,
            },
          )
        }
        confirmLabel="Purge"
      />
      {list.isSuccess && list.data.items.length > 0 && !showDeleted && (
        <DataTable
          density="compact"
          onRowClick={(row) => setOpenId(Number(row.id))}
          columns={[
            { key: 'path', label: 'URL', mono: true },
            { key: 'title', label: 'Title' },
            { key: 'kind', label: 'Kind' },
            { key: 'locale', label: 'Locale' },
            { key: 'state', label: 'State' },
          ]}
          rows={list.data.items.map((p) => ({
            id: p.id,
            path: p.path,
            title: p.title,
            kind: isAppCopy(p.path) ? 'app copy · not on the site' : p.page_type.replace(/_/g, ' '),
            locale: p.locale,
            state: <StatusChip tone={statusTone(p.status)} label={p.status} />,
          }))}
          footer={`${list.data.total} page${list.data.total === 1 ? '' : 's'}`}
        />
      )}
    </>
  );
}

/**
 * The app's own copy kept as CMS rows — the store listing and the help articles. The app reads
 * them; the site renders none of them, so their path is a key, not a URL anybody can open.
 * Mirrors the server's `cms.is_app_copy`. Exported for its own test.
 */
export function isAppCopy(path: string): boolean {
  return /^(\/hi)?\/(store-listing|help)\/[^/]+$/.test(path);
}

/** The overrides a search result shows for this page, as text fields. Robots blank = template. */
const SEO_TEXT = [
  ['title_override', 'Title override'],
  ['description_override', 'Description override'],
  ['h1_override', 'H1 override'],
  ['canonical_url', 'Canonical URL'],
  ['og_title', 'Share title'],
  ['og_description', 'Share description'],
] as const;
type SeoText = (typeof SEO_TEXT)[number][0];
const TEMPLATE_ROBOTS = 'from the template';

const IST: Intl.DateTimeFormatOptions = {
  timeZone: 'Asia/Kolkata',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
};

/** "13 May, 09:30 am IST" — the console's clock is IST whatever the browser's is. */
export const istStamp = (at: string) => `${new Date(at).toLocaleString('en-IN', IST)} IST`;

/**
 * Whether the live site has what is published (admin doc 23 §3.1): when the last build ran, its
 * manifest drift, and whether content was written after it started. `site_behind` is job 19's own
 * reading, so this panel cannot call the site current while the job is about to rebuild it.
 */
export function deploySentence(b: SeoBudget): string {
  const build = b.last_build;
  if (!build) return 'No build has reported yet — the live site is whatever was last deployed by hand.';
  const shipped = `Last deployed ${istStamp(build.finished_at)} · ${build.file_count} files`;
  const drift = build.mismatched ? ` · drift of ${build.drift} files against the registry` : '';
  const behind = b.site_behind
    ? ` · content changed ${b.content_changed_at ? istStamp(b.content_changed_at) : 'since'} — the live site catches up at the next deploy`
    : ' · the live site has everything published';
  return `${shipped}${drift}${behind}`;
}

function DeployStatus() {
  const { scope, toast } = useConsole();
  const budget = useSeoBudget();
  const rebuild = useRebuildSite();
  const [asking, setAsking] = useState(false);
  return (
    <Section
      title="Live site"
      note="Publishing writes rows; a deploy is what puts them on bookmypandiit.com."
      actions={
        <ScopeAction scope={scope} need="catalogue_content" size="sm" variant="secondary" onClick={() => setAsking(true)}>
          Rebuild site
        </ScopeAction>
      }
    >
      <ConfirmAction
        open={asking}
        title="Rebuild the live site?"
        phrase="REBUILD"
        tone="warn"
        reason
        consequences={[
          'Renders every prerendered page against the live database, asserts the file budget, deploys to Cloudflare Pages, purges the zone cache.',
          'Runs even when nothing changed since the last deploy. A build over the budget stops before it deploys.',
        ]}
        onCancel={() => setAsking(false)}
        onConfirm={(reason) =>
          rebuild.mutate(reason, {
            onSuccess: (out) => {
              setAsking(false);
              toast(
                out.queued
                  ? { tone: 'success', message: `Site rebuild queued${out.job_id ? ` · job ${out.job_id}` : ''}` }
                  : { tone: 'error', message: 'A rebuild is already waiting in the queue — nothing new was queued' },
              );
            },
            onError: (error) => toast({ tone: 'error', message: errorMessage(error) }),
          })
        }
        confirmLabel={rebuild.isPending ? 'Queueing…' : 'Rebuild'}
      />
      {budget.isPending && <LoadingState message="Reading the last deploy…" />}
      {budget.isError && <ErrorState title="Could not read the last deploy" cause={errorMessage(budget.error)} />}
      {budget.isSuccess && (
        <div style={{ display: 'grid', gap: 'var(--sp-xs)', fontSize: 'var(--fs-admin-ui)' }}>
          <span style={{ color: budget.data.site_behind ? 'var(--c-warn-text)' : 'var(--c-ink2)' }}>
            {deploySentence(budget.data)}
          </span>
          <span style={{ color: 'var(--c-ink3)' }}>{meterSentence(budget.data)}</span>
        </div>
      )}
    </Section>
  );
}

/**
 * The page's own SEO override (doc 40 §4.6). The server stores the whole set on every save, so the
 * fields this card does not edit (the share image and the JSON-LD override) go back unchanged.
 */
function PageSeo({ cmsPageId }: { cmsPageId: number }) {
  const meta = usePageMeta(cmsPageId);
  const row = meta.data?.meta ?? null;
  return (
    <Section
      title="Search appearance"
      note="A field set here beats the page's SEO template; clearing every field hands the page back to it."
    >
      {meta.isPending && <LoadingState message="Reading the overrides…" />}
      {meta.isError && <ErrorState title="Could not read the overrides" cause={errorMessage(meta.error)} />}
      {meta.isSuccess && <PageSeoForm key={JSON.stringify(row)} cmsPageId={cmsPageId} row={row} />}
    </Section>
  );
}

function PageSeoForm({ cmsPageId, row }: { cmsPageId: number; row: SeoPageMetaIn | null }) {
  const { scope, toast } = useConsole();
  const put = usePutPageMeta();
  const [fields, setFields] = useState<Record<SeoText, string>>(
    () => Object.fromEntries(SEO_TEXT.map(([key]) => [key, row?.[key] ?? ''])) as Record<SeoText, string>,
  );
  const [robots, setRobots] = useState(row?.robots ?? TEMPLATE_ROBOTS);

  const send = (body: SeoPageMetaIn, message: string) =>
    put.mutate({ cmsPageId, body }, { onSuccess: () => toast({ tone: 'success', message }) });
  const save = () =>
    send(
      {
        ...Object.fromEntries(SEO_TEXT.map(([key]) => [key, fields[key].trim() || null])),
        robots: robots === TEMPLATE_ROBOTS ? null : robots,
        og_image_key: row?.og_image_key ?? null,
        json_ld_override: row?.json_ld_override ?? null,
      },
      'Overrides saved',
    );

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
      {SEO_TEXT.map(([key, label]) => (
        <Input
          key={key}
          density="admin"
          label={label}
          value={fields[key]}
          onChange={(v) => setFields({ ...fields, [key]: v })}
          helper={key === 'canonical_url' ? 'A path on this site, or a URL on its own origin' : undefined}
        />
      ))}
      <Select
        density="admin"
        label="Robots"
        value={robots}
        onChange={setRobots}
        options={[TEMPLATE_ROBOTS, ...ROBOTS]}
      />
      {put.isError && <p style={{ color: 'var(--c-danger)', margin: 0 }}>{errorMessage(put.error)}</p>}
      <div style={{ display: 'flex', gap: 'var(--sp-md)' }}>
        <ScopeAction scope={scope} need="catalogue_content" size="sm" disabled={put.isPending} onClick={save}>
          Save overrides
        </ScopeAction>
        {row !== null && (
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            variant="ghost"
            disabled={put.isPending}
            onClick={() => send({}, 'Overrides cleared · the template titles this page again')}
          >
            Clear overrides
          </ScopeAction>
        )}
      </div>
    </div>
  );
}
